<template>
  <div v-outside-click="closePopup">
    <div class="kk-profile">
      <div v-if="user" class="kk-profile__general-info">
        <div class="kk-profile__profile-contacts">
          <h2>{{ user.username }}</h2>
          <div class="kk-profile__profile-contact">
            <span class="kk-profile__profile-email">
              {{ user.attributes.email }}
            </span>

            <i v-if="user.attributes.email_verified" class="icon-check-circle kk-profile__contact--verified"></i>
            <span v-else class="kk-profile__contact--not-verified" @click="verifyEmail()">
              {{ $t("profile.emailNotVerified") }}
            </span>
          </div>
        </div>
        <img :src="require('@/assets/avatar.png')" class="kk-profile__profile-img" />
      </div>

      <hr class="kk-profile__general-info--hr" />

      <ul class="kk-profile__list">
        <li class="kk-profile__list--item">Edit Profile</li>
        <li>Reset Password</li>
        <li><subscribe-ad :hideable="false"></subscribe-ad></li>
        <hr class="kk-profile__profile--hr" />
        <li>
          Language
          <span data-test-id="language-selection"> <language /> </span>
        </li>
        <li>Settings</li>
        <hr class="kk-profile__profile--hr" />
        <li data-test-id="sign-out" @click="signOut">Sign Out</li>
      </ul>
    </div>
  </div>
</template>

<script>
import { Auth } from "aws-amplify";
import { useVuelidate } from "@vuelidate/core";
import { minLength, required } from "@vuelidate/validators";
import SubscribeAd from "../SubscribeAd.vue";
import Language from "./Language.vue";

export default {
  components: {
    SubscribeAd,
    Language,
  },
  validations() {
    return {
      currentPassword: { required, minLength: minLength(8) },
      newPassword: { required, minLength: minLength(8) },
    };
  },
  emits: ["close-popup"],
  data() {
    return {
      v$: useVuelidate(),
      user: null,
      showPassword: false,
      currentPassword: "",
      newPassword: "",
      verificationAttr: "",
      verificationCode: "",
    };
  },
  computed: {
    isSubmitDisabled() {
      return this.v$.$invalid; // Double negation for boolean conversion
    },
    passwordInputType() {
      return this.showPassword ? "text" : "password";
    },
  },
  mounted() {
    this.reloadUser();
  },
  methods: {
    signOut() {
      this.$store.dispatch("smartSearch/searchAndCall", { value: "logout", i18n: this.$i18n });
    },
    validateCurrentPassword() {
      this.v$.currentPassword.$touch();
    },
    validateNewPassword() {
      this.v$.newPassword.$touch();
    },
    closePopup() {
      this.$emit("close-popup");
    },
    verifyEmail() {
      Auth.currentAuthenticatedUser().then((user) =>
        Auth.verifyUserAttribute(user, "email")
          .then((resp) => {
            this.verificationAttr = "email";

            this.$store.dispatch("addToast", {
              severity: "info",
              life: 3000,
              ...this.$tm("profile.verifyEmail.info"),
            });
          })
          .catch((err) => {
            console.error(err);

            this.$store.dispatch("addToast", {
              severity: "error",
              life: 3000,
              ...this.$tm("profile.verifyEmail.failed"),
              ...{ detail: err.message },
            });
          })
      );
    },
    submitVerify() {
      Auth.currentAuthenticatedUser().then((user) =>
        Auth.verifyUserAttributeSubmit(user, this.verificationAttr, this.verificationCode)
          .then((resp) => {
            this.verificationAttr = "";
            this.reloadUser();
          })
          .catch((err) => {
            console.error(err);

            this.$store.dispatch("addToast", {
              severity: "error",
              life: 3000,
              ...this.$tm("profile.verifyEmail.confirm.success"),
              ...{ detail: err.message },
            });
          })
      );
    },
    changePassword() {
      if (!this.v$.$error) {
        Auth.currentAuthenticatedUser().then((user) =>
          Auth.changePassword(user, this.currentPassword, this.newPassword)
            .then((resp) => {
              this.currentPassword = "";
              this.newPassword = "";

              this.$store.dispatch("addToast", {
                severity: "success",
                life: 3000,
                ...this.$tm("profile.changePassword.success"),
              });

              this.closePopup();
            })
            .catch((err) => {
              console.error(err);

              this.$store.dispatch("addToast", {
                severity: "error",
                life: 3000,
                ...this.$tm("profile.changePassword.failed"),
                ...{ detail: err.message },
              });
            })
        );
      }
    },
    reloadUser() {
      Auth.currentAuthenticatedUser()
        .then((user) => (this.user = user))
        .catch((err) => {
          console.error(err);

          this.$store.dispatch("addToast", {
            severity: "error",
            life: 3000,
            ...this.$tm("profile.loadInfo.failed"),
            ...{ detail: err.message },
          });
        });
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../assets/styles/variables";
.kk-profile {
  padding: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  background: @kk-color-white;
  position: absolute;
  right: 1.1rem;
  z-index: 110;
  top: 3.9rem;
  min-width: 16rem;

  &__general-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    &--hr {
      margin: 0.7rem 0;
      border: 1px solid #0832770a;
    }
  }

  &__profile--hr {
    margin: 0.2rem 0px;
    border: 1px solid #0832770a;
  }

  &__profile-img {
    width: 3.5rem;
  }
  &__profile-contact {
    display: flex;
    align-items: center;
    gap: 3px;
  }
  &__profile-contacts {
    margin-top: 0.8rem;
    display: flex;
    flex-direction: column;
    gap: 5px;

    h2 {
      font-size: 0.85remx;
      color: #083277;
    }
  }

  &__profile-email {
    font-size: 10px;
    color: rgba(8, 50, 119, 0.56);
  }

  &__contact--verified {
    color: @kk-color-green;
  }

  &__contact--not-verified {
    color: initial;
    background-color: @kk-color-background;
    font-size: 0.65rem;
    padding: 0.0625rem 0.25rem;
    border-radius: 0.25rem;
    cursor: pointer;
  }

  &__contact-verification {
    display: block;
    margin-top: 1rem;
    width: 100%;

    > form {
      display: flex;
      align-items: center;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    font-family: Roboto;
    list-style: none;
    font-size: 0.9rem;
    padding-left: 0.6rem;
    font-weight: 500;

    li {
      color: rgba(8, 50, 119, 0.73);
      text-align: left;
      display: flex;
      align-items: center;

      &:hover {
        color: rgba(8, 50, 119, 0.93);
        cursor: pointer;
      }
    }
  }
}
</style>
